//#region src/error.ts
/**
* Wrap an unknown thrown/rejected value into a real `Error` so observability
* tools (Sentry, structured logs) can group, stack-trace, and display it.
*
* - Real `Error` instances pass through untouched (subclass identity preserved).
* - Plain objects (GraphQL errors, third-party rejections, etc.) stringify to
*   `"[object Object]"` via `String()`, which is useless for grouping. We pull
*   a `message`-like field if present, JSON-serialize otherwise, and fall back
*   to `"[unserializable object]"` for circular/unserializable payloads.
* - Class instances (URL, Response, …) often have meaningful `toString` and
*   empty enumerable props, so they fall through to `String(value)`.
*
* The original value is attached via `cause` so the source is reachable in
* Sentry as a sub-exception regardless of which branch ran.
*/
function toError(value) {
	if (value instanceof Error) return value;
	if (isPlainObject(value)) {
		const message = typeof value.message === "string" && value.message || safeJsonStringify(value) || "[unserializable object]";
		const wrapped = new Error(message, { cause: value });
		if (typeof value.name === "string" && value.name) wrapped.name = value.name;
		return wrapped;
	}
	return new Error(String(value), { cause: value });
}
function isPlainObject(value) {
	if (value === null || typeof value !== "object") return false;
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}
function safeJsonStringify(value) {
	try {
		const result = JSON.stringify(value);
		return typeof result === "string" ? result : null;
	} catch {
		return null;
	}
}
//#endregion
export { toError };
